<template>
    <section class="kanban-coluna" :data-coluna="coluna.id">
        <header class="kanban-coluna__cabecalho">
            <h3 class="kanban-coluna__titulo">
                <span class="kanban-coluna__nome" :title="coluna.descricao">{{ coluna.descricao }}</span>
                <!-- `total` é o total da coluna no banco, não o tamanho do array
                     carregado: a coluna mostra "Lead (128)" com 20 cards na tela. -->
                <span class="kanban-coluna__total">{{ coluna.total }}</span>
            </h3>

            <!-- Ícone com menu, e não Select aberto: o Select do PrimeVue mede
                 149px para caber "Mais recentes", e 149 + título + contagem não
                 cabem nos 264px úteis do cabeçalho. Ele vazava para fora da
                 coluna e cobria o começo do nome da coluna vizinha — era assim
                 que "Instalação" aparecia como "alação" na tela do cliente. -->
            <Button
                icon="pi pi-sort-alt"
                text
                rounded
                size="small"
                class="kanban-coluna__ordenar"
                :aria-label="rotuloOrdenacao"
                aria-haspopup="menu"
                :aria-controls="menuOrdenacaoId"
                v-tooltip.top="rotuloOrdenacao"
                @click="abrirMenu"
            />
            <Menu :id="menuOrdenacaoId" ref="menuOrdenacao" :model="itensOrdenacao" popup />
        </header>

        <div
            ref="lista"
            class="kanban-coluna__cards"
            role="region"
            tabindex="0"
            :aria-label="`${coluna.descricao}: ${coluna.total} contratos, ${coluna.cards.length} carregados`"
            :aria-busy="carregando"
        >
            <KanbanCard
                v-for="card in coluna.cards"
                :key="card.id"
                :contrato="card"
                :coluna="coluna.id"
                @abrir="$emit('abrir', $event)"
            />

            <p v-if="!coluna.cards.length && !carregando" class="kanban-coluna__vazia">
                Nenhum contrato nesta etapa
            </p>

            <div ref="sentinela" aria-hidden="true"></div>

            <p class="kanban-coluna__estado" role="status" aria-live="polite">
                <template v-if="carregando">
                    <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
                    Carregando mais contratos de {{ coluna.descricao }}…
                </template>
                <template v-else-if="coluna.cards.length && !coluna.pagina.tem_mais">
                    Fim da coluna
                </template>
            </p>
        </div>
    </section>
</template>

<script>
import Button from 'primevue/button';
import Menu from 'primevue/menu';
import KanbanCard from './KanbanCard.vue';

export default {
    name: 'KanbanColuna',
    components: { Button, Menu, KanbanCard },
    props: {
        coluna: { type: Object, required: true },
        carregando: { type: Boolean, default: false },
    },
    emits: ['ordenar', 'carregar-mais', 'abrir'],
    data() {
        return {
            sentinelaVisivel: false,
            opcoesOrdenacao: [
                { valor: 'created_at:desc', rotulo: 'Mais recentes' },
                { valor: 'created_at:asc', rotulo: 'Mais antigos' },
                { valor: 'id:desc', rotulo: 'Maior ID' },
                { valor: 'id:asc', rotulo: 'Menor ID' },
            ],
        };
    },
    computed: {
        ordenacaoAtual() {
            return `${this.coluna.ordenacao.campo}:${this.coluna.ordenacao.direcao}`;
        },
        menuOrdenacaoId() {
            return `kanban-ordenacao-${this.coluna.id}`;
        },
        /**
         * O rótulo carrega a ordenação vigente porque ela saiu da tela junto com
         * o Select: um ícone sozinho não diz por qual campo a coluna está
         * ordenada. Vale para o leitor de tela e para o tooltip do mouse.
         */
        rotuloOrdenacao() {
            const atual = this.opcoesOrdenacao.find(o => o.valor === this.ordenacaoAtual);
            return `Ordenar coluna ${this.coluna.descricao}. Atual: ${atual?.rotulo || 'padrão'}`;
        },
        itensOrdenacao() {
            return this.opcoesOrdenacao.map(opcao => ({
                label: opcao.rotulo,
                // `pi-fw` é só largura fixa: mantém os rótulos alinhados sob o
                // item marcado, sem CSS extra.
                icon: opcao.valor === this.ordenacaoAtual ? 'pi pi-check' : 'pi pi-fw',
                command: () => this.$emit('ordenar', opcao.valor),
            }));
        },
    },
    watch: {
        // O observer só dispara na mudança de visibilidade. Terminada uma
        // página, a sentinela pode continuar visível (coluna alta, página
        // curta) e nada mais chegaria; reavaliar aqui mantém o scroll vivo.
        carregando(valor) {
            if (!valor) this.$nextTick(this.talvezCarregar);
        },
        // Reordenar recomeça a coluna do primeiro card. Sem voltar o scroll, o
        // usuário fica no meio de uma lista que não é mais a que ele rolou.
        ordenacaoAtual() {
            if (this.$refs.lista) this.$refs.lista.scrollTop = 0;
        },
    },
    mounted() {
        this.observador = new IntersectionObserver(([entrada]) => {
            this.sentinelaVisivel = entrada.isIntersecting;
            this.talvezCarregar();
        }, { root: this.$refs.lista, rootMargin: '150px' });

        this.observador.observe(this.$refs.sentinela);
    },
    beforeUnmount() {
        this.observador?.disconnect();
    },
    methods: {
        abrirMenu(evento) {
            this.$refs.menuOrdenacao.toggle(evento);
        },
        talvezCarregar() {
            if (this.sentinelaVisivel && !this.carregando && this.coluna.pagina.tem_mais) {
                this.$emit('carregar-mais');
            }
        },
    },
};
</script>

<style scoped>
.kanban-coluna {
    display: flex;
    flex-direction: column;
    flex: 0 0 auto;
    width: 280px;
    max-width: 85vw;
    scroll-snap-align: start;
    background: var(--kanban-coluna-bg);
    border: 1px solid var(--surface-border);
    border-radius: var(--border-radius);

    /* Os dois temas invertem a escala: no claro o card é mais claro que a
       coluna, no escuro é mais claro também — e para isso os tokens trocam de
       lugar, porque `--surface-0` é o extremo claro em um e o extremo escuro no
       outro. O card lê estas variáveis por herança do DOM. */
    --kanban-coluna-bg: var(--surface-100);
    --kanban-card-bg: var(--surface-0);
    --kanban-card-hover-bg: var(--surface-50);
}
.my-app-dark .kanban-coluna {
    --kanban-coluna-bg: var(--surface-0);
    --kanban-card-bg: var(--surface-100);
    --kanban-card-hover-bg: var(--surface-200);
}

/* Lateral do card, por etapa — o card só lê `--kanban-card-borda`.

   Método, porque escolher tom a tom já falhou uma vez (deu dois azuis e um
   cinza): sete matizes espaçados na roda OKLCH, e dentro de cada tema a
   luminância e o croma resolvidos por busca, maximizando a MENOR distância
   entre os 21 pares — medida também com deuteranopia e protanopia simuladas
   (Viénot), que é onde matiz sozinho não salva ninguém.

   Duas paletas, uma por tema. Uma paleta só teria de caber na faixa estreita de
   luminância que passa de 3:1 contra os dois fundos ao mesmo tempo, e com a
   luminância presa sobra só o matiz para separar. Por tema, a luminância volta
   a ser dimensão de separação — e é a que sobrevive ao daltonismo.

   A tag de vencimento entrou na conta como repulsor, não como vizinha: as três
   severidades que o card renderiza (danger/warn/info) são pontos fixos de que
   toda cor daqui mantém distância. Sem isso, a primeira busca colocou a lateral
   de "Cotação" a ΔE 3.0 do vermelho de "Vencido" — prazo e etapa pintados da
   mesma cor no mesmo card.

   Medido (ΔE OKLab ×100):
   - claro:  pior par 13.8 (13.5 com daltonismo); contraste mínimo 3.11:1
   - escuro: pior par 15.0 (15.0 com daltonismo); contraste mínimo 3.02:1
   - distância mínima até uma tag de vencimento: 13.1 (claro), 13.2 (escuro)

   Cor aqui é reforço: o cabeçalho da coluna nomeia a etapa. */
.kanban-coluna[data-coluna='lead']        { --kanban-card-borda: #3b2afd; }
.kanban-coluna[data-coluna='cotacao']     { --kanban-card-borda: #900906; }
.kanban-coluna[data-coluna='provedor']    { --kanban-card-borda: #ad844b; }
.kanban-coluna[data-coluna='assinatura']  { --kanban-card-borda: #d90df4; }
.kanban-coluna[data-coluna='agendamento'] { --kanban-card-borda: #017186; }
.kanban-coluna[data-coluna='instalacao']  { --kanban-card-borda: #444e05; }
.kanban-coluna[data-coluna='concluido']   { --kanban-card-borda: #419b75; }

.my-app-dark .kanban-coluna[data-coluna='lead']        { --kanban-card-borda: #096af7; }
.my-app-dark .kanban-coluna[data-coluna='cotacao']     { --kanban-card-borda: #e6261d; }
.my-app-dark .kanban-coluna[data-coluna='provedor']    { --kanban-card-borda: #936f33; }
.my-app-dark .kanban-coluna[data-coluna='assinatura']  { --kanban-card-borda: #9a4feb; }
.my-app-dark .kanban-coluna[data-coluna='agendamento'] { --kanban-card-borda: #148280; }
.my-app-dark .kanban-coluna[data-coluna='instalacao']  { --kanban-card-borda: #fcdc1f; }
.my-app-dark .kanban-coluna[data-coluna='concluido']   { --kanban-card-borda: #8bf8a9; }
.kanban-coluna__cabecalho {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
    padding: 8px;
    border-bottom: 1px solid var(--surface-border);
    /* Cinto de segurança: nem o cabeçalho nem nada dentro dele pinta fora da
       coluna. O vazamento anterior só era visível porque o filho transbordava
       para cima da coluna vizinha. */
    overflow: hidden;
}
.kanban-coluna__titulo {
    display: flex;
    align-items: baseline;
    gap: 6px;
    /* `min-width: 0` é o que permite o título encolher: sem ele um flex item
       nunca fica menor que o conteúdo e empurra o resto para fora. */
    min-width: 0;
    margin: 0;
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--p-text-color, var(--surface-900));
}
.kanban-coluna__nome {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.kanban-coluna__total {
    /* A contagem nunca encolhe nem quebra: "2559" cortado vira outro número. */
    flex: 0 0 auto;
    padding: 0 7px;
    border-radius: 10px;
    background: var(--surface-300);
    color: var(--surface-800);
    font-size: 0.72rem;
    line-height: 1.6;
}
/* O botão de texto do PrimeVue herda o primary do Aura (verde-azulado), que não
   existe na paleta deste sistema — o primary aqui é laranja. Neutro no repouso,
   cor de texto no hover: é controle de apoio, não chamada de ação. */
.kanban-coluna__cabecalho .kanban-coluna__ordenar {
    flex: 0 0 auto;
    width: 2rem;
    height: 2rem;
    color: var(--surface-600);
}
.kanban-coluna__cabecalho .kanban-coluna__ordenar:hover {
    color: var(--p-text-color, var(--surface-900));
    background: var(--surface-200);
}
.kanban-coluna__ordenar:focus-visible {
    outline: 2px solid var(--primary-color);
    outline-offset: 1px;
}
.kanban-coluna__cards {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 8px;
    overflow-y: auto;
    /* Altura fixa é o que faz cada coluna rolar sozinha; sem ela a página
       inteira rolaria e não haveria scroll por coluna. */
    height: calc(100vh - 340px);
    min-height: 240px;
}
.kanban-coluna__cards:focus-visible {
    outline: 2px solid var(--primary-color);
    outline-offset: -2px;
}
.kanban-coluna__vazia,
.kanban-coluna__estado {
    margin: 4px 0 0;
    text-align: center;
    font-size: 0.75rem;
    color: var(--surface-600);
    min-height: 1rem;
}
</style>
